import { useEffect, useState } from "react";
import { Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import CardNew from "components/molecules/Card/CardNew";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Badge from "components/atoms/Badge/Badge";
import moment from "moment";
import { renderAddressV2InfoForHeaders } from "helpers/addressV2Helpers";
import CopyToClipboard from "components/molecules/CopyToClipboard/CopyToClipboard";
import Button from "components/v2/Button/Button";
import PropTypes from "prop-types";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { ReactComponent as EditPencil } from "assets/images/icons/EditPencil.svg";
import { ReactComponent as BoltIcon } from "assets/images/icons/BoltBlue.svg";
import { DateTimeFormat, formatDate, parseDate } from "helpers/dateTimeHelper";
import { getLabelWithTranslationFallback } from "helpers/getLabel";
import { Link, useParams } from "react-router-dom";
import EditTimersDialog from "./EditTimersDialog";
import ConsolidatedModal from "./ConsolidatedModal";
import "../ShipmentQuotes.scss";

function QuoteInfo({ data, loadingData, fetchData, setIsConsolidated, isQuickRequest }) {
  
  const { id } = useParams();
  const { data: shipmentBasicData } = useGetShipmentBasic(id, {
    enabled: !isQuickRequest && !!id,
    refetchOnMount: false,
  });

  const {
    confirmation_date,
    confirmation_time,
    confirmation_date_remaining_time_display,
    confirmation_date_seconds_remaining,
    quoting_deadline_date,
    quoting_deadline_time,
    quoting_deadline_remaining_time_display,
    quoting_deadline_seconds_remaining,
    payment_location,
    shipment_id,
    requestor,
    requestor_email,
  } = data.organization || {};
  const {
    pickup_address_name,
    pickup_country_code,
    pickup_city,
    pickup_date,
    pickup_time,
    pickup_end_date,
    pickup_end_time,
    pickup_zipcode,
    pickup_seaport,
    pickup_airport,
    stop1_date,
    stop1_time,
    stop1_end_date,
    stop1_end_time,
    stop1_datetime,
    stop1_zipcode,
    stop1_address_name,
    stop1_country_code,
    stop1_city,
    stop1_airport,
    stop1_seaport,
    address_v2,
  } = data.schedule || {};

  const { renderPickupAddress, renderDeliveryAddress } = renderAddressV2InfoForHeaders({ data });

  const { quick_request_reference } = data?.quick_request_details?.[0] || {};

  const intl = useIntl();
  const isConsolidated = data?.quick_request_details?.length > 1;

  useEffect(() => {
    if (isConsolidated) {
      setIsConsolidated(true);
    }
  }, [isConsolidated]);

  const header = (title) => (
    <p
      className="font-b5 text-secondary"
      style={{
        padding: 0,
        marginBottom: "7px",
      }}
    >
      {title}
    </p>
  );

  const [showEditTimersDialog, setShowEditTimersDialog] = useState(false);
  const [hoverConfirmation, setHoverConfirmation] = useState(false);
  const [hoverQuotingDeadline, setHoverQuotingDeadline] = useState(false);
  const [showConsolidatedModal, setShowConsolidatedModal] = useState(false);

  const requestStatus = shipmentBasicData?.organization?.request_status;

  const isCanceled = requestStatus === "Canceled" || requestStatus === "Auto canceled";

  const hideEditDatesButton = [
    "In transit",
    "Incomplete",
    "Delayed",
    "Delivered",
    "Booked",
    "Archive",
    "Canceled",
    "Auto canceled",
  ].includes(requestStatus);

  const isQuoteRequestBooked = !data?.available_for_booking;
  const renderDeadline = (isConfirmationDate = false) => {
    const confirmationDate = `${confirmation_date} ${confirmation_time}`;
    const quotingDeadlineDate = `${quoting_deadline_date} ${quoting_deadline_time}`;
    const deadlineDate = isConfirmationDate ? confirmationDate : quotingDeadlineDate;
    const secondsRemaining = isConfirmationDate ? confirmation_date_seconds_remaining : quoting_deadline_seconds_remaining;
    const timeRemaining = isConfirmationDate
      ? confirmation_date_remaining_time_display
      : quoting_deadline_remaining_time_display;
    let badgeType = "primary";
    if (secondsRemaining > 0 && secondsRemaining < 300) {
      badgeType = "danger";
    } else if (secondsRemaining > 0 && secondsRemaining < 3600) {
      badgeType = "warning";
    }

    const formattedDeadline = moment(new Date(deadlineDate)).format("DD MMM YYYY, HH:mm");
    const formattedConfirmation = moment(new Date(confirmationDate)).format("DD MMM YYYY, HH:mm");

    return (
      <div className="d-flex align-items-center">
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 250, hide: 0 }}
          overlay={<Tooltip id="button-tooltip">{isConfirmationDate ? formattedConfirmation : formattedDeadline}</Tooltip>}
        >
          <div className="position-relative">
            {secondsRemaining < 0 ? (
              <div style={{ whiteSpace: "nowrap" }}>
                <Badge
                  type="danger"
                  label={intl.formatMessage({ id: "quote-request.deadlinePassed", defaultMessage: "Deadline has passed" })}
                />
              </div>
            ) : (
              <div style={{ whiteSpace: "nowrap" }}>
                <Badge type={badgeType} label={`in ${timeRemaining}`} />
              </div>
            )}
          </div>
        </OverlayTrigger>
      </div>
    );
  };
  const getRequestTimerStatusLabel = () => {
    if (isCanceled) {
      if (requestStatus === "Canceled") {
        return intl.formatMessage({
          id: "general.canceled",
          defaultMessage: "Canceled",
        });
      }
      if (requestStatus === "Auto canceled") {
        return intl.formatMessage({
          id: "general.autoCanceled",
          defaultMessage: "Auto-canceled",
        });
      }
    } else if (isQuoteRequestBooked) {
      return intl.formatMessage({
        id: "quote-request.closed",
        defaultMessage: "Closed",
      });
    }
  };

  if (loadingData)
    return (
      <Row lg={12} className="m-0">
        <CardNew className="w-100 border-radius-top-0">
          <LoadingSkeleton rows={1} />
        </CardNew>
      </Row>
    );

  return (
    <Row lg={12} className="m-0">
      <EditTimersDialog
        fetchData={fetchData}
        isOpen={showEditTimersDialog}
        close={() => setShowEditTimersDialog(false)}
        requestId={shipment_id}
        pickupDate={pickup_date}
        requestTimers={{
          confirmation_date,
          confirmation_time: confirmation_time?.slice(0, -3),
          quoting_deadline_date,
          quoting_deadline_time: quoting_deadline_time?.slice(0, -3),
        }}
        quoting_deadline_seconds_remaining={quoting_deadline_seconds_remaining}
      />
      <ConsolidatedModal
        data={data?.quick_request_details}
        isOpen={showConsolidatedModal}
        close={() => setShowConsolidatedModal(false)}
      />
      <CardNew className="w-100 border-radius-top-0">
        <CardNew.Body>
          <Row>
            <Col className="col-12 mb-3" md>
              {header(intl.formatMessage({ id: "general.createdBy", defaultMessage: "Created By" }))}
              <p
                className="p-0 m-0 cursor-pointer"
                style={{ fontSize: "15px", transform: "translateY(-3px)" }}
                onKeyDown={() => {}}
                onClick={() => {
                  window.open(`mailto:${requestor_email}`);
                }}
              >
                <OverlayTrigger
                  placement="bottom"
                  delay={{ show: 250, hide: 0 }}
                  overlay={<Tooltip id="button-tooltip">{`Mail to: ${requestor_email}`}</Tooltip>}
                >
                  <span>{requestor}</span>
                </OverlayTrigger>
              </p>
              <div className="text-secondary mb-2 font-b2">{payment_location}</div>
              {quick_request_reference &&
                (isConsolidated ? (
                  <Button
                    label={
                      <FormattedMessage id="general.viewConsolidatedOrders" defaultMessage="View consolidated orders" />
                    }
                    size="sm"
                    variant="link-color"
                    onClick={() => setShowConsolidatedModal(true)}
                  />
                ) : (
                  <Link to={`/shipment/${quick_request_reference}/quick`} style={{ fontSize: 12 }}>
                    <BoltIcon className="qr-icon" />
                    {quick_request_reference}
                  </Link>
                ))}
            </Col>

            <Col
              className="col-12 mb-3"
              md
              onMouseEnter={() => setHoverQuotingDeadline(true)}
              onMouseLeave={() => setHoverQuotingDeadline(false)}
            >
              <div className="d-flex">
                <span>
                  {header(
                    getLabelWithTranslationFallback("Quoting deadline", {
                      translation: intl.formatMessage({
                        id: "new-request.quote-confirmation.schedule.quotingDeadline",
                        defaultMessage: "Quoting deadline",
                      }),
                      returnAsString: true,
                      withoutInfo: true,
                    }),
                  )}
                </span>
                {hoverQuotingDeadline && !hideEditDatesButton && (
                  <EditPencil className="ml-2 cursor-pointer" onClick={() => setShowEditTimersDialog(true)} />
                )}
              </div>

              <div className="d-flex">
                {isCanceled || isQuoteRequestBooked ? (
                  <Badge type="secondary" label={getRequestTimerStatusLabel()} />
                ) : (
                  renderDeadline()
                )}
              </div>
            </Col>
            <Col
              className="col-12 mb-3"
              md
              onMouseEnter={() => setHoverConfirmation(true)}
              onMouseLeave={() => setHoverConfirmation(false)}
            >
              <div className="d-flex">
                <span>
                  {header(
                    getLabelWithTranslationFallback("Confirmation time", {
                      translation: intl.formatMessage({
                        id: "new-request.quote-confirmation.schedule.confirmationTime",
                        defaultMessage: "Confirmation time",
                      }),
                      returnAsString: true,
                      withoutInfo: true,
                    }),
                  )}
                </span>
                {hoverConfirmation && !hideEditDatesButton && (
                  <EditPencil className="ml-2 cursor-pointer" onClick={() => setShowEditTimersDialog(true)} />
                )}
              </div>
              <div className="d-flex">
                {isCanceled || isQuoteRequestBooked ? (
                  <Badge type="secondary" label={getRequestTimerStatusLabel()} />
                ) : (
                  renderDeadline(true)
                )}
              </div>
            </Col>
            <Col className="col-12 mb-3" md>
              {header(
                intl.formatMessage({
                  id: "new-request.schedule.cargoPickup",
                  defaultMessage: "Pickup",
                }),
              )}
              <div
              >
                {Object.keys(address_v2 ?? {}).length > 0 ? (
                  renderPickupAddress()
                ) : (
                  <CopyToClipboard
                    copyOnly
                    text={[
                      pickup_address_name,
                      pickup_city,
                      pickup_airport,
                      pickup_seaport,
                      pickup_country_code,
                      pickup_zipcode,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  >
                    <p className="font-b2">{pickup_address_name}</p>
                    <p className="font-b2 mt-1">
                      {[pickup_zipcode, pickup_city, pickup_airport, pickup_seaport, pickup_country_code]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  </CopyToClipboard>
                )}
                {pickup_end_date && pickup_end_time ? (
                  <p className="font-b2 mt-1">
                    {`${formatDate(`${pickup_date} ${pickup_time}`, DateTimeFormat.fullDateTimeFormat)} till ${
                      pickup_end_date !== pickup_date
                        ? formatDate(`${pickup_end_date} ${pickup_end_time}`, DateTimeFormat.fullDateTimeFormat)
                        : formatDate(parseDate(pickup_end_time, "HH:mm:ss"), DateTimeFormat.timeFormat)
                    }`}
                  </p>
                ) : (
                  <p className="font-b2 mt-1">
                    {formatDate(`${pickup_date} ${pickup_time}`, DateTimeFormat.fullDateTimeFormat)}
                  </p>
                )}
              </div>
            </Col>
            <Col className="col-12 mb-3" md>
              {header(
                intl.formatMessage({
                  id: "new-request.schedule.cargoDelivery",
                  defaultMessage: "Delivery",
                }),
              )}
              <div
              >
                {Object.keys(address_v2 ?? {}).length > 0 ? (
                  renderDeliveryAddress()
                ) : (
                  <CopyToClipboard
                    copyOnly
                    text={[stop1_address_name, stop1_city, stop1_airport, stop1_seaport, stop1_country_code, stop1_zipcode]
                      .filter(Boolean)
                      .join(", ")}
                  >
                    <p className="font-b2">{stop1_address_name}</p>
                    <p className="font-b2 mt-1">
                      {[stop1_zipcode, stop1_city, stop1_airport, stop1_seaport, stop1_country_code]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  </CopyToClipboard>
                )}
                {stop1_datetime === "ASAP" && (
                  <p className="font-b2 mt-1">
                    <FormattedMessage
                      id="new-request.schedule.defaultCargoReadyDeliveryOption.asap"
                      defaultMessage="ASAP Delivery"
                    />
                  </p>
                )}
                {stop1_datetime !== "ASAP" &&
                  (stop1_end_date && stop1_end_time ? (
                    <p className="font-b2 mt-1">
                      {`${formatDate(`${stop1_date} ${stop1_time}`, DateTimeFormat.fullDateTimeFormat)} till ${
                        stop1_end_date !== stop1_date
                          ? formatDate(`${stop1_end_date} ${stop1_end_time}`, DateTimeFormat.fullDateTimeFormat)
                          : formatDate(parseDate(stop1_end_time, "HH:mm:ss"), DateTimeFormat.timeFormat)
                      }
                     `}
                    </p>
                  ) : (
                    <p className="font-b2 mt-1">
                      {formatDate(`${stop1_date} ${stop1_time}`, DateTimeFormat.fullDateTimeFormat)}
                    </p>
                  ))}
              </div>
            </Col>
          </Row>
        </CardNew.Body>
      </CardNew>
    </Row>
  );
}

QuoteInfo.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  loadingData: PropTypes.bool.isRequired,
  fetchData: PropTypes.func.isRequired,
  setIsConsolidated: PropTypes.func,
  isQuickRequest: PropTypes.bool,
};

QuoteInfo.defaultProps = {
  setIsConsolidated: () => {},
  isQuickRequest: false,
};

export default QuoteInfo;
